import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ManageUplines() {
  const { user, loading: authLoading } = useAuth();
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [allApprovedForUpline, setAllApprovedForUpline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUplineCode, setSelectedUplineCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Fetch approved users (potential uplines)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/pending`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setAllApprovedForUpline(data.approved || []);
      setApprovedUsers(data.approved || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUpline = async () => {
    if (!selectedUser || !selectedUplineCode) {
      setError('Please select both user and upline');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/assign-upline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedUser,
          upline_affiliate_code: selectedUplineCode,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to assign upline');
      }

      setSuccessMessage(`Upline assigned: ${result.upline.email}`);
      setSelectedUser('');
      setSelectedUplineCode('');
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-on-background">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-margin-mobile pt-24">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-headline-lg text-on-background mb-2">Manage Uplines</h1>
          <p className="text-body-md text-on-background/60">Assign or change upline (sponsor) for approved users. Changes can be made until user is placed in tree.</p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-500 text-green-700 p-4 rounded-lg mb-6 text-body-sm">
            ✓ {successMessage}
          </div>
        )}

        <div className="bg-surface border border-outline rounded-lg p-6">
          <h2 className="text-title-lg text-on-background font-medium mb-6">Assign Upline</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* User Selection */}
            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Select Approved User
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-outline rounded-lg text-on-background focus:outline-none focus:border-primary"
              >
                <option value="">-- Choose User --</option>
                {approvedUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}` : u.email} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Upline Selection by Affiliate Code */}
            <div>
              <label className="block text-body-sm font-medium text-on-background mb-2">
                Upline Affiliate Code
              </label>
              <input
                type="text"
                value={selectedUplineCode}
                onChange={(e) => setSelectedUplineCode(e.target.value.toUpperCase())}
                placeholder="e.g., WELLIS-abc12345"
                className="w-full px-4 py-2 bg-background border border-outline rounded-lg text-on-background focus:outline-none focus:border-primary"
              />
              <p className="text-body-xs text-on-background/60 mt-1">
                Enter the affiliate code of the upline user
              </p>
            </div>
          </div>

          {/* Available Uplines Reference */}
          <div className="mt-6 pt-6 border-t border-outline">
            <h3 className="text-body-sm font-medium text-on-background mb-3">Available Upline Codes</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {allApprovedForUpline.map((u) => (
                <div key={u.id} className="p-3 bg-background rounded-lg text-body-sm">
                  <div className="font-mono font-medium text-primary">{u.affiliate_code}</div>
                  <div className="text-on-background/70">{u.email}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Button */}
          <div className="mt-6 flex gap-4">
            <button
              onClick={handleAssignUpline}
              disabled={!selectedUser || !selectedUplineCode || submitting}
              className="px-6 py-3 bg-primary text-on-primary rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {submitting ? 'Assigning...' : 'Assign Upline'}
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="px-6 py-3 bg-outline text-on-background rounded-lg font-medium hover:bg-outline/80 transition-colors"
            >
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
